import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ComparisonTabs from '../components/ComparisonTabs';
import SoapNote from '../components/SoapNote';
import VitalsGrid from '../components/VitalsGrid';
import SbarNote from '../components/SbarNote';
import CortiNoteDisplay from '../components/CortiNoteDisplay';
import ActionBar from '../components/ActionBar';
import MedicationTaskActionBar from '../components/MedicationTaskActionBar';
import AdmissionNote from '../components/AdmissionNote';
import DischargeNote from '../components/DischargeNote';
import MedicationAdminNote from '../components/MedicationAdminNote';
import FluidBalanceNote from '../components/FluidBalanceNote';
import CarePlanNote from '../components/carePlan/CarePlanNote';
import PatientHeaderBar from '../components/review/PatientHeaderBar';
import { AdverseReactionBanner } from '../components/review/ClinicalFlagsPanel';
import { collectFlagsFromNotes } from '../utils/clinicalFlags';
import TranscriptViewer from '../components/TranscriptViewer';
import NoteTypeSectionHeader from '../components/review/NoteTypeSectionHeader';
import { REVIEW_PAGE_BG } from '../components/review/reviewStyles';
import { getCortiDisplaySections } from '../services/cortiService';
import { getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { applyKnownPatientToNote, toNotePatientContext } from '../utils/patientContext';
import { resolvePatientForNote } from '../utils/patientMatch';
import {
  getHandoverPatients,
  isMultiPatientHandover,
  toSinglePatientHandoverNote,
  applyKnownPatientToHandoverNote,
} from '../utils/handoverUtils';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { HoldToRecordButton } from '../contexts/HoldToRecordContext';
import { ReviewModalFloatingSlotContext } from '../layouts/ReviewModal';
import {
  cleanTranscript,
  detectNoteTypes,
  generateNote,
  updateNurseNotesFromCorrection,
} from '../services/openAIService';
import { validateNote } from '../utils/jsonValidator';
import { getVitalKeysWithValues } from '../utils/taskCompletionFromNote';
import { getMedicationTaskDoseMismatchFlags } from '../utils/medicationTaskDose';
import {
  consolidateOutstandingTasksForEncounter,
} from '../utils/taskUtils';
import {
  applyMarCompanionProgressDefaults,
  ensureProgressNoteWithMedications,
} from '../utils/generationPipeline';
import {
  applyLateEntryMetadata,
  deriveCareTime,
  getCurrentCareTime,
} from '../utils/lateEntry';
import { CareTimeEditor } from '../components/NoteTimingMeta';
import { noteHasDocumentationContent } from '../utils/taskDocumentation';
import {
  applySpokenExtrasToExistingNotes,
  applySpokenMedicationExtras,
  filterVitalsConsumedByMarExtras,
  getNoteMedicationSubmitBlocker,
  isLikelyMedicationExtrasOnlyTranscript,
  mergeMedicationExtras,
} from '../utils/drugCategory';
import {
  buildNotGivenMedicationNote,
} from '../utils/medicationDeviation';
import {
  getProgressNoteBody,
  setProgressNoteBody,
} from '../utils/progressNoteBody';
import NoteApplyingSkeleton from '../components/review/NoteApplyingSkeleton';
import { createPilotMetricsTracker } from '../utils/pilotMetrics';

function getMedsFromNote(note) {
  if (note?.medications_administered?.length) return note.medications_administered;
  return note?.medications || [];
}

function mergeMedicationAdminNotes(existingNote, incomingNote, transcript) {
  const existingMeds = getMedsFromNote(existingNote);
  const incomingMeds = getMedsFromNote(incomingNote);
  const count = Math.max(existingMeds.length, incomingMeds.length, 1);
  const merged = Array.from({ length: count }, (_, index) => {
    const base = existingMeds[index] || existingMeds[0] || {};
    const incoming = incomingMeds[index] || incomingMeds[0] || {};
    return applySpokenMedicationExtras(mergeMedicationExtras(base, incoming), transcript);
  });
  return {
    ...(existingNote || {}),
    ...(incomingNote || {}),
    medications_administered: merged,
    medications: merged,
    unverified_medications: merged.filter((med) => med.confidence === 'low'),
  };
}
function getPatientFromNote(note) {
  return note?.patient || { name: null, room: null, age: null, sex: null, details_source: 'transcript' };
}

function buildInitialGeneratedNotes({
  generatedNotes,
  noteType,
  openaiNote,
  companionVitalsNote,
  openaiError,
}) {
  if (generatedNotes?.length) {
    return generatedNotes.map((entry) => ({ ...entry }));
  }

  const notes = [];
  if (openaiNote || openaiError) {
    notes.push({ type: noteType, note: openaiNote, error: openaiError || null });
  }
  if (companionVitalsNote) {
    notes.push({ type: 'vitals', note: companionVitalsNote, error: null });
  }
  return notes;
}

function noteEntryId(groupId, type, index) {
  return index === 0 ? groupId : `${groupId}-${type}`;
}

function vitalsNoteHasVisibleCards(note) {
  if (!note) return false;
  const vitals = note.vitals || {};
  const hasValue = (value) => value != null && value !== '';
  if (Object.values(vitals).some(hasValue)) return true;
  if (Array.isArray(note._manualVisibleVitalsKeys)) {
    return note._manualVisibleVitalsKeys.length > 0;
  }
  return false;
}

function renderNoteByType(type, note, onChange, encounterDateTime, options = {}) {
  const {
    hideProgressExtractedVitals = false,
    vitalsFlags = [],
    allowDelete = true,
    onDeleteNote = null,
    careTime = null,
    careDayOffset = null,
    documentationTime = null,
    onCareTimeChange = null,
  } = options;

  switch (type) {
    case 'progress_note':
      return (
        <SoapNote
          note={note}
          onChange={onChange}
          onDelete={allowDelete ? onDeleteNote : null}
          datetime={encounterDateTime}
          hideExtractedVitals={hideProgressExtractedVitals}
        />
      );
    case 'vitals':
      return (
        <VitalsGrid
          note={note}
          onChange={onChange}
          datetime={encounterDateTime}
          flags={vitalsFlags}
          allowDelete={allowDelete}
        />
      );
    case 'handover':
      return (
        <SbarNote
          note={note}
          onChange={onChange}
          onDelete={allowDelete ? onDeleteNote : null}
          datetime={encounterDateTime}
          careTime={careTime}
          careDayOffset={careDayOffset}
          documentationTime={documentationTime}
          onCareTimeChange={onCareTimeChange}
        />
      );
    case 'admission':
      return <AdmissionNote note={note} onChange={onChange} />;
    case 'discharge':
      return (
        <DischargeNote
          note={note}
          onChange={onChange}
          onDelete={allowDelete ? onDeleteNote : null}
        />
      );
    case 'medication_admin':
      return (
        <MedicationAdminNote
          note={note}
          onChange={onChange}
          allowDelete={allowDelete}
        />
      );
    case 'fluid_balance':
      return (
        <FluidBalanceNote
          note={note}
          onChange={onChange}
          allowDelete={allowDelete}
        />
      );
    case 'care_plan':
      return (
        <CarePlanNote
          note={note}
          onChange={onChange}
          allowDelete={allowDelete}
        />
      );    default:
      return null;
  }
}

function hasAtLeastOneManualField(type, note) {
  return noteHasDocumentationContent(type, note);
}

export default function NoteReview({
  noteId,
  encounterGroupId,
  patientId,
  encounterDateTime,
  transcript,
  transcriptRaw,
  transcriptCleaned,
  noteType,
  generatedNotes: initialGeneratedNotes,
  openaiNote: initialOpenaiNote,
  companionVitalsNote: initialCompanionVitalsNote,
  cortiNote: initialCortiNote,
  cortiRaw,
  cortiFailed,
  openaiError,
  initialActiveTab = 'openai',
  manualTaskCompletionType = null,
  requireRecordingBeforeSubmit = false,
  requireDocumentationBeforeSubmit = false,
  hideFloatingMic = false,
  hideEmptyTranscript = false,
  taskTitle = null,
  taskPatient = null,
  onGeneratedNotesChange,
  onSaveNote,
  onSaveCarePlan,
  onDiscard,
  onSaveComplete,
  clinicalIds = null,
  recordingDurationSeconds = null,
}) {
  const pilotMetricsRef = useRef(null);
  if (!pilotMetricsRef.current) {
    const initialJson = initialOpenaiNote || initialGeneratedNotes?.[0]?.note || null;
    pilotMetricsRef.current = createPilotMetricsTracker(initialJson);
  }

  const recordPilotEdit = useCallback(() => {
    pilotMetricsRef.current?.recordEdit();
  }, []);
  const { admittedPatients, getPatientTasks, registerTaskDocTranscriptHandler, nurse } = useSession();
  const { language, t } = useLanguage();
  const groupId = encounterGroupId || noteId || Date.now().toString();

  const recordPatient = useMemo(() => {
    if (patientId) return getPatientById(patientId, admittedPatients);

    const hintNote = initialGeneratedNotes?.find((entry) => entry.note)?.note
      || initialOpenaiNote
      || initialCompanionVitalsNote;
    const resolved = resolvePatientForNote({
      admittedPatients,
      transcript: transcriptCleaned || transcript,
      patientHints: hintNote?.patient,
    });
    return resolved?.patient || null;
  }, [
    patientId,
    admittedPatients,
    initialGeneratedNotes,
    initialOpenaiNote,
    initialCompanionVitalsNote,
    transcript,
    transcriptCleaned,
  ]);
  const patientContext = useMemo(
    () => (recordPatient ? toNotePatientContext(recordPatient) : null),
    [recordPatient]
  );

  const [modelTab, setModelTab] = useState(initialActiveTab);
  const [generatedNotes, setGeneratedNotes] = useState(() => {
    const notes = buildInitialGeneratedNotes({
      generatedNotes: initialGeneratedNotes,
      noteType,
      openaiNote: initialOpenaiNote,
      companionVitalsNote: initialCompanionVitalsNote,
      openaiError,
    });
    if (!patientContext) return notes;
    return notes.map((entry) =>
      entry.note
        ? {
            ...entry,
            note:
              entry.type === 'handover'
                ? applyKnownPatientToHandoverNote(entry.note, patientContext)
                : applyKnownPatientToNote(entry.note, patientContext),
          }
        : entry
    );
  });
  const [cortiNote, setCortiNote] = useState(() => {
    if (!initialCortiNote || !patientContext) return initialCortiNote;
    return applyKnownPatientToNote(initialCortiNote, patientContext);
  });
  const [cortiSections, setCortiSections] = useState(
    () => getCortiDisplaySections(initialCortiNote, cortiRaw)
  );
  const [saveError, setSaveError] = useState('');
  const [micBusy, setMicBusy] = useState(false);
  const generatedNotesRef = useRef(generatedNotes);
  generatedNotesRef.current = generatedNotes;
  const floatingSlot = useContext(ReviewModalFloatingSlotContext);
  const sessionGeneratedNotes = useMemo(
    () => generatedNotes.filter((entry) => entry.type !== 'care_plan'),
    [generatedNotes]
  );
  const carePlanEntries = useMemo(
    () => generatedNotes.filter((entry) => entry.type === 'care_plan' && entry.note),
    [generatedNotes]
  );
  const hasMultipleNotes = generatedNotes.length > 1;
  const primaryNote = generatedNotes[0]?.note || initialOpenaiNote;
  const activeNote = modelTab === 'openai' ? primaryNote : cortiNote;
  const activeModel = modelTab === 'openai' ? 'openai' : 'corti';
  const reviewDocumentationTime = useMemo(() => {
    if (encounterDateTime instanceof Date) return encounterDateTime.toISOString();
    const parsed = new Date(encounterDateTime);
    return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
  }, [encounterDateTime]);

  const handleCareTimeChange = useCallback(
    (type, timingUpdate) => {
      setGeneratedNotes((prev) =>
        prev.map((entry) => {
          if (entry.type !== type || !entry.note) return entry;
          return {
            ...entry,
            note: {
              ...entry.note,
              ...timingUpdate,
              documentation_time: reviewDocumentationTime,
            },
          };
        })
      );
    },
    [reviewDocumentationTime]
  );

  const detectedTypes = generatedNotes.map((entry) => entry.type);

  const patient = useMemo(() => {
    if (patientContext) return patientContext;
    for (const entry of generatedNotes) {
      if (entry.type === 'handover' && entry.note) {
        const firstPatient = getHandoverPatients(entry.note)[0];
        if (firstPatient) {
          return {
            name: firstPatient.patient_name,
            room: firstPatient.room,
            details_source: 'transcript',
          };
        }
      }
      if (entry.note) return getPatientFromNote(entry.note);
    }
    return getPatientFromNote(activeNote);
  }, [patientContext, generatedNotes, activeNote]);

  const handoverNoteEntry = useMemo(
    () => generatedNotes.find((entry) => entry.type === 'handover' && entry.note),
    [generatedNotes]
  );
  const multiPatientHandover = handoverNoteEntry?.note
    ? isMultiPatientHandover(handoverNoteEntry.note)
    : false;

  const handoverPatients = useMemo(() => {
    if (!multiPatientHandover || !handoverNoteEntry?.note) return [];
    return getHandoverPatients(handoverNoteEntry.note);
  }, [multiPatientHandover, handoverNoteEntry]);

  const displayPatient = useMemo(() => {
    if (multiPatientHandover) {
      const patients = handoverPatients;
      return {
        name: `${patients.length} patients`,
        room: null,
        details_source: 'transcript',
      };
    }
    if (!recordPatient) return patient;
    return {
      ...patient,
      name: getPatientDisplayName(recordPatient, language),
    };
  }, [multiPatientHandover, handoverPatients, recordPatient, patient, language]);

  const patientTasks = useMemo(() => {
    if (!recordPatient?.id) return [];
    return getPatientTasks(recordPatient.id);
  }, [recordPatient, getPatientTasks]);

  const medicationDoseMismatchFlags = useMemo(() => {
    const marEntry = generatedNotes.find((entry) => entry.type === 'medication_admin' && entry.note);
    if (!marEntry || !patientTasks.length) return [];
    return getMedicationTaskDoseMismatchFlags(patientTasks, marEntry.note, encounterDateTime, t);
  }, [generatedNotes, patientTasks, encounterDateTime, t]);

  const allFlags = useMemo(
    () => [
      ...collectFlagsFromNotes(generatedNotes.map((entry) => entry.note).filter(Boolean)),
      ...medicationDoseMismatchFlags,
    ],
    [generatedNotes, medicationDoseMismatchFlags]
  );

  const syncPatientAcrossNotes = useCallback(
    (updatedPatient) => {
      setGeneratedNotes((prev) =>
        prev.map((entry) =>
          entry.note ? { ...entry, note: { ...entry.note, patient: updatedPatient } } : entry
        )
      );
      if (cortiNote) {
        setCortiNote({ ...cortiNote, patient: updatedPatient });
      }
    },
    [cortiNote]
  );

  const handleRemoveNoteType = useCallback((type) => {
    setGeneratedNotes((prev) => {
      const next = prev.filter((entry) => entry.type !== type);
      onGeneratedNotesChange?.(next);
      return next;
    });
  }, [onGeneratedNotesChange]);

  const handleNoteEdit = useCallback((type, updated) => {
    recordPilotEdit();
    setGeneratedNotes((prev) => {
      let next = prev.map((entry) => (entry.type === type ? { ...entry, note: updated } : entry));
      if (type === 'vitals' && !vitalsNoteHasVisibleCards(updated)) {
        next = next.filter((entry) => entry.type !== 'vitals');
      }
      onGeneratedNotesChange?.(next);
      return next;
    });
  }, [onGeneratedNotesChange, recordPilotEdit]);

  const handleCortiSectionChange = (index, content) => {
    const updated = [...cortiSections];
    updated[index] = { ...updated[index], content };
    setCortiSections(updated);
  };

  const buildNotePayload = (payloadId, type, noteContent, model) => {
    const documentationTime = reviewDocumentationTime;
    const withDefaults = {
      ...(noteContent || {}),
      datetime: documentationTime,
      care_time:
        noteContent?.care_time
        || deriveCareTime(noteContent)
        || getCurrentCareTime(),
      care_day_offset:
        noteContent?.care_day_offset === 0 || noteContent?.care_day_offset === 1
          ? noteContent.care_day_offset
          : null,
    };
    const withTiming = applyLateEntryMetadata(withDefaults, documentationTime);
    const primaryNote = withTiming;
    const pilotMetrics = pilotMetricsRef.current?.buildSavePayload(primaryNote) || null;
    return {
      id: payloadId,
      patient_id: recordPatient?.id || patientId,
      patient_name: recordPatient ? getPatientDisplayName(recordPatient, language) : patient.name,
      patient_room: patient.room,
      note_type: type,
      datetime: documentationTime,
      documentation_time: documentationTime,
      care_time: withTiming.care_time,
      care_day_offset: withTiming.care_day_offset,
      is_late_entry: withTiming.is_late_entry,
      late_entry_diff_minutes: withTiming.late_entry_diff_minutes,
      encounter_group_id: groupId,
      note_content: withTiming,
      openaiNote: withTiming,
      transcript: transcriptCleaned || transcript,
      transcript_raw: transcriptRaw || transcript,
      transcript_cleaned: transcriptCleaned || transcript,
      pdf_saved: false,
      model_used: model,
      generatedNotes,
      cortiNote,
      cortiRaw,
      cortiFailed,
      openaiError: generatedNotes.find((e) => e.type === type)?.error || openaiError,
      activeTab: modelTab,
      clinicalIds,
      recordingDurationSeconds,
      pilotMetrics,
    };
  };

  const saveHandoverNote = useCallback(
    (parentNote, model) => {
      const patients = getHandoverPatients(parentNote);

      patients.forEach((patientEntry, index) => {
        const singleNote = toSinglePatientHandoverNote(parentNote, patientEntry);
        const resolved = resolvePatientForNote({
          admittedPatients,
          transcript: transcriptCleaned || transcript,
          patientHints: { name: patientEntry.patient_name, room: patientEntry.room },
        });
        const resolvedPatient = resolved?.patient;

        onSaveNote({
          ...buildNotePayload(
            patients.length > 1 ? `${groupId}-handover-${index}` : noteId || groupId,
            'handover',
            singleNote,
            model
          ),
          patient_id: resolvedPatient?.id || recordPatient?.id || patientId || null,
          patient_name: resolvedPatient
            ? getPatientDisplayName(resolvedPatient, language)
            : patientEntry.patient_name || patient.name,
          patient_room: patientEntry.room || patient.room,
        });
      });
    },
    [
      admittedPatients,
      buildNotePayload,
      groupId,
      language,
      noteId,
      onSaveNote,
      patient.name,
      patient.room,
      patientId,
      recordPatient,
      transcript,
      transcriptCleaned,
    ]
  );

  const handleSave = () => {
    const medBlocker = sessionGeneratedNotes
      .map((entry) =>
        entry.type === 'medication_admin' && entry.note
          ? getNoteMedicationSubmitBlocker(entry.note)
          : null
      )
      .find(Boolean);
    if (medBlocker?.messageKey) {
      setSaveError(t(medBlocker.messageKey));
      return;
    }

    const requireDoc = requireDocumentationBeforeSubmit || requireRecordingBeforeSubmit;
    if (requireDoc) {
      const documented = sessionGeneratedNotes.some((entry) =>
        noteHasDocumentationContent(entry.type, entry.note)
      );
      if (!documented) {
        const blocker = sessionGeneratedNotes
          .map((entry) =>
            entry.type === 'medication_admin' && entry.note
              ? getNoteMedicationSubmitBlocker(entry.note)
              : null
          )
          .find(Boolean);
        if (blocker?.messageKey) {
          setSaveError(t(blocker.messageKey));
        }
        return;
      }
    }
    const manualType = manualTaskCompletionType || null;
    if (manualType && sessionGeneratedNotes.length === 1) {
      const noteForValidation = sessionGeneratedNotes[0]?.note;
      if (!hasAtLeastOneManualField(manualType, noteForValidation)) {
        if (manualType === 'progress_note') {
          setSaveError(t('fillSoapSection'));
        } else if (manualType === 'vitals') {
          setSaveError(t('fillVitalValue'));
        } else if (manualType === 'medication_admin') {
          setSaveError(t('fillMedicationField'));
        } else if (manualType === 'handover') {
          setSaveError(t('fillSbarSection'));
        } else {
          setSaveError(t('fillOneField'));
        }
        return;
      }
    }
    setSaveError('');

    const patientIdForSave = recordPatient?.id || patientId;
    carePlanEntries.forEach((entry) => {
      if (!patientIdForSave || !entry.note) return;
      const withTiming = applyLateEntryMetadata(
        {
          ...entry.note,
          care_time:
            entry.note.care_time
            || deriveCareTime(entry.note)
            || getCurrentCareTime(),
        },
        reviewDocumentationTime
      );
      onSaveCarePlan?.({
        patient_id: patientIdForSave,
        note_content: withTiming,
        documentationTime: reviewDocumentationTime,
        care_time: withTiming.care_time,
        care_day_offset: withTiming.care_day_offset,
      });
    });

    const notesForSave = consolidateOutstandingTasksForEncounter(sessionGeneratedNotes);
    if (!notesForSave.length) {
      onSaveComplete?.();
      return;
    }

    const handoverEntry = notesForSave.find((entry) => entry.type === 'handover' && entry.note);

    if (handoverEntry?.note) {
      saveHandoverNote(
        handoverEntry.note,
        hasMultipleNotes || modelTab === 'openai' ? 'openai' : activeModel
      );

      notesForSave
        .filter((entry) => entry.type !== 'handover' && entry.note)
        .forEach((entry, index) => {
          const entryId = noteEntryId(groupId, entry.type, index + 1);
          onSaveNote(buildNotePayload(entryId, entry.type, entry.note, 'openai'));
        });

      onSaveComplete?.();
      return;
    }

    if (hasMultipleNotes) {
      notesForSave.forEach((entry, index) => {
        if (!entry.note) return;
        const entryId = noteEntryId(groupId, entry.type, index);
        onSaveNote(buildNotePayload(entryId, entry.type, entry.note, 'openai'));
      });
    } else {
      const entry = sessionGeneratedNotes[0];
      const noteContent = modelTab === 'openai' ? entry?.note : cortiNote;
      const type = entry?.type || noteType;
      onSaveNote({
        ...buildNotePayload(noteId || groupId, type, noteContent, activeModel),
        openaiNote: modelTab === 'openai' ? entry?.note : undefined,
        cortiNote,
      });
    }
    onSaveComplete?.();
  };

  const handleSaveDeviation = ({ reasonId, reasonText }) => {
    const entry = sessionGeneratedNotes.find((item) => item.type === 'medication_admin' && item.note)
      || sessionGeneratedNotes[0];
    if (!entry?.note) {
      setSaveError(t('fillMedicationField'));
      return;
    }

    const documentedAt = reviewDocumentationTime || new Date().toISOString();
    const deviationNote = buildNotGivenMedicationNote(entry.note, {
      reasonId,
      reasonText,
      nurseId: nurse?.id || null,
      documentedAt,
    });

    setSaveError('');
    onSaveNote({
      ...buildNotePayload(noteId || groupId, 'medication_admin', deviationNote, 'openai'),
      openaiNote: deviationNote,
      completion_outcome: 'not_given',
      deviation_reason: reasonText,
    });
    onSaveComplete?.();
  };

  const handleMicTranscript = useCallback(
    async (liveTranscript) => {
      if (!liveTranscript?.trim()) return;
      setMicBusy(true);
      recordPilotEdit();
      setSaveError('');
      const forcedType = manualTaskCompletionType || noteType || null;
      const isTaskDocumentation = Boolean(
        requireDocumentationBeforeSubmit || requireRecordingBeforeSubmit
      );

      try {
        const sourceText = liveTranscript.trim();

        // Task documentation keeps the existing fill/merge path (MAR extras, append).
        // Review of already-generated notes uses a surgical patch like doctor review.
        if (!isTaskDocumentation) {
          const currentNotes = generatedNotesRef.current || [];
          if (currentNotes.some((entry) => entry?.note)) {
            const next = await updateNurseNotesFromCorrection(currentNotes, sourceText);
            if (!Array.isArray(next)) {
              throw new Error(t('failedProcessRecording'));
            }
            setGeneratedNotes(next);
            onGeneratedNotesChange?.(next);
            setModelTab('openai');
            return;
          }
        }

        // Prefer filling missing MAR extras (e.g. "bp 120/80" → bp_before)
        // before detect/generate so we do not create a duplicate vitals card.
        let marExtras = { filledAny: false, filledKeys: [] };
        setGeneratedNotes((prev) => {
          marExtras = applySpokenExtrasToExistingNotes(prev, sourceText);
          if (!marExtras.filledAny) return prev;
          onGeneratedNotesChange?.(marExtras.notes);
          return marExtras.notes;
        });
        if (marExtras.filledAny && isLikelyMedicationExtrasOnlyTranscript(sourceText)) {
          setModelTab('openai');
          return;
        }

        // Task documentation: known note type — do not detect types.
        if (isTaskDocumentation && forcedType) {
          if (forcedType === 'progress_note') {
            let cleaned = sourceText;
            try {
              cleaned = (await cleanTranscript(sourceText)) || sourceText;
            } catch {
              cleaned = sourceText;
            }
            setGeneratedNotes((prev) => {
              const next = prev.map((entry) => {
                if (entry.type !== 'progress_note' || !entry.note) return entry;
                const existing = getProgressNoteBody(entry.note).trim();
                const combined = existing ? `${existing}\n\n${cleaned}` : cleaned;
                return {
                  ...entry,
                  note: setProgressNoteBody(entry.note, combined),
                  error: null,
                };
              });
              if (!next.some((entry) => entry.type === 'progress_note')) {
                next.unshift({
                  type: 'progress_note',
                  note: setProgressNoteBody(
                    { note_type: 'progress_note', soap: {}, patient: {} },
                    cleaned
                  ),
                  error: null,
                });
              }
              onGeneratedNotesChange?.(next);
              return next;
            });
            setModelTab('openai');
            return;
          }

          const raw = await generateNote(sourceText, forcedType);
          let note = validateNote(raw, forcedType);
          if (patientContext) {
            note = forcedType === 'handover'
              ? applyKnownPatientToHandoverNote(note, patientContext)
              : applyKnownPatientToNote(note, patientContext);
          }
          note.datetime = note.datetime || new Date().toISOString();

          setGeneratedNotes((prev) => {
            const byType = new Map(prev.map((entry) => [entry.type, entry]));
            const existing = byType.get(forcedType);
            if (forcedType === 'vitals' && existing?.note && note) {
              const mergedVitals = {
                ...existing.note.vitals,
                ...note.vitals,
              };
              const visibleKeys = [
                ...new Set([
                  ...(existing.note._manualVisibleVitalsKeys || []),
                  ...getVitalKeysWithValues(mergedVitals),
                ]),
              ];
              byType.set(forcedType, {
                ...existing,
                note: {
                  ...existing.note,
                  ...note,
                  vitals: mergedVitals,
                  _manualVisibleVitalsKeys: visibleKeys.length
                    ? visibleKeys
                    : existing.note._manualVisibleVitalsKeys,
                },
                error: null,
              });
            } else if (forcedType === 'medication_admin' && existing?.note && note) {
              byType.set(forcedType, {
                ...existing,
                note: mergeMedicationAdminNotes(existing.note, note, sourceText),
                error: null,
              });
            } else if (forcedType === 'medication_admin' && note) {
              const meds = getMedsFromNote(note).map((med) =>
                applySpokenMedicationExtras(med, sourceText)
              );
              byType.set(forcedType, {
                type: forcedType,
                note: {
                  ...note,
                  medications_administered: meds,
                  medications: meds,
                },
                error: null,
              });
            } else {
              byType.set(forcedType, { type: forcedType, note, error: null });
            }
            const next = Array.from(byType.values());
            onGeneratedNotesChange?.(next);
            return next;
          });
          setModelTab('openai');
          return;
        }

        const cleaned = await cleanTranscript(sourceText);
        const detected = ensureProgressNoteWithMedications(
          await detectNoteTypes(cleaned || sourceText)
        );
        if (!detected.length) {
          setMicBusy(false);
          return;
        }
        const nextNotes = await Promise.all(
          detected.map(async (type) => {
            const raw = await generateNote(cleaned || sourceText, type);
            let note = validateNote(raw, type);
            if (patientContext) {
              note = type === 'handover'
                ? applyKnownPatientToHandoverNote(note, patientContext)
                : applyKnownPatientToNote(note, patientContext);
            }
            note.datetime = note.datetime || new Date().toISOString();
            return { type, note, error: null };
          })
        );
        const hasMar = nextNotes.some((entry) => entry.type === 'medication_admin' && entry.note);
        let notesForMerge = hasMar
          ? nextNotes.map((entry) =>
              entry.type === 'progress_note' && entry.note
                ? { ...entry, note: applyMarCompanionProgressDefaults(entry.note) }
                : entry
            )
          : nextNotes;
        // Strip vitals already applied onto MAR missing fields (e.g. BP before).
        notesForMerge = notesForMerge
          .map((entry) => filterVitalsConsumedByMarExtras(entry, marExtras.filledKeys))
          .filter(Boolean);
        if (!notesForMerge.length) {
          setModelTab('openai');
          return;
        }
        setGeneratedNotes((prev) => {
          const byType = new Map(prev.map((entry) => [entry.type, entry]));
          notesForMerge.forEach((entry) => {
            const existing = byType.get(entry.type);
            if (entry.type === 'vitals' && existing?.note && entry.note) {
              const mergedVitals = {
                ...existing.note.vitals,
                ...entry.note.vitals,
              };
              const visibleKeys = [
                ...new Set([
                  ...(existing.note._manualVisibleVitalsKeys || []),
                  ...getVitalKeysWithValues(mergedVitals),
                ]),
              ];
              byType.set(entry.type, {
                ...existing,
                note: {
                  ...existing.note,
                  ...entry.note,
                  vitals: mergedVitals,
                  _manualVisibleVitalsKeys: visibleKeys.length ? visibleKeys : existing.note._manualVisibleVitalsKeys,
                },
                error: entry.error,
              });
              return;
            }
            if (entry.type === 'medication_admin' && existing?.note && entry.note) {
              byType.set(entry.type, {
                ...existing,
                note: mergeMedicationAdminNotes(existing.note, entry.note, sourceText),
                error: entry.error,
              });
              return;
            }
            byType.set(entry.type, entry);
          });
          const next = Array.from(byType.values());
          onGeneratedNotesChange?.(next);
          return next;
        });
        setModelTab('openai');
      } catch (err) {
        setSaveError(err?.message || t('failedProcessRecording'));
      } finally {
        setMicBusy(false);
      }
    },
    [
      manualTaskCompletionType,
      noteType,
      onGeneratedNotesChange,
      patientContext,
      recordPilotEdit,
      requireDocumentationBeforeSubmit,
      requireRecordingBeforeSubmit,
      t,
    ]
  );

  useEffect(() => {
    if (!(requireDocumentationBeforeSubmit || requireRecordingBeforeSubmit)) return undefined;
    return registerTaskDocTranscriptHandler?.(handleMicTranscript);
  }, [
    handleMicTranscript,
    registerTaskDocTranscriptHandler,
    requireDocumentationBeforeSubmit,
    requireRecordingBeforeSubmit,
  ]);

  const renderContent = () => {
    if (!generatedNotes.length) {
      return null;
    }

    const hasDedicatedVitalsNote = generatedNotes.some(
      (entry) => entry.type === 'vitals' && entry.note
    );
    const vitalsFlags = allFlags.filter((flag) => flag.category === 'vitals');

    return (
      <>
        {generatedNotes.map((entry, index) => {
          const noteForDisplay = entry.note
            ? applyLateEntryMetadata(
                {
                  ...entry.note,
                  care_time:
                    entry.note.care_time
                    || deriveCareTime(entry.note)
                    || getCurrentCareTime(),
                },
                reviewDocumentationTime
              )
            : null;

          const isMultiHandover =
            entry.type === 'handover' && entry.note && isMultiPatientHandover(entry.note);

          return (
          <section key={`${entry.type}-${index}`} className={index > 0 ? 'mt-6' : ''}>
            {entry.type !== 'progress_note' && entry.type !== 'handover' && entry.type !== 'discharge' ? (
              <NoteTypeSectionHeader noteType={entry.type} />
            ) : null}
            {entry.error && (
              <div className="mb-2.5 rounded-xl border border-critical bg-red-50 p-3 text-sm text-critical">
                Generation failed: {entry.error}
              </div>
            )}
            {noteForDisplay && !isMultiHandover ? (
              <CareTimeEditor
                className="mb-2.5"
                careTime={noteForDisplay.care_time}
                careDayOffset={noteForDisplay.care_day_offset}
                documentationTime={reviewDocumentationTime}
                onChange={(timingUpdate) => handleCareTimeChange(entry.type, timingUpdate)}
              />
            ) : null}
            {entry.note &&
              renderNoteByType(
                entry.type,
                entry.note,
                (updated) => handleNoteEdit(entry.type, updated),
                encounterDateTime,
                {
                  hideProgressExtractedVitals: hasDedicatedVitalsNote && entry.type === 'progress_note',
                  vitalsFlags: entry.type === 'vitals' ? vitalsFlags : [],
                  allowDelete: !(
                    requireDocumentationBeforeSubmit || requireRecordingBeforeSubmit
                  ) || Boolean(
                    String(transcriptCleaned || transcriptRaw || transcript || '').trim()
                  ),
                  onDeleteNote: () => handleRemoveNoteType(entry.type),
                  careTime: isMultiHandover ? noteForDisplay?.care_time : null,
                  careDayOffset: isMultiHandover ? noteForDisplay?.care_day_offset : null,
                  documentationTime: isMultiHandover ? reviewDocumentationTime : null,
                  onCareTimeChange: isMultiHandover
                    ? (timingUpdate) => handleCareTimeChange(entry.type, timingUpdate)
                    : null,
                }
              )}
          </section>
          );
        })}
      </>
    );
  };

  const isTaskDocumentationMode = Boolean(
    requireDocumentationBeforeSubmit || requireRecordingBeforeSubmit
  );

  const isMedicationTaskDoc = Boolean(
    isTaskDocumentationMode
    && (
      manualTaskCompletionType === 'medication_admin'
      || sessionGeneratedNotes.some((entry) => entry.type === 'medication_admin')
    )
  );

  const medicationSubmitBlocker = useMemo(() => {
    for (const entry of sessionGeneratedNotes) {
      if (entry.type !== 'medication_admin' || !entry.note) continue;
      const blocker = getNoteMedicationSubmitBlocker(entry.note);
      if (blocker) return blocker;
    }
    return null;
  }, [sessionGeneratedNotes]);

  const saveDisabled = useMemo(() => {
    if (medicationSubmitBlocker) return true;
    if (!isTaskDocumentationMode) return false;
    const hasContent = sessionGeneratedNotes.some((entry) =>
      noteHasDocumentationContent(entry.type, entry.note)
    );
    return !hasContent;
  }, [isTaskDocumentationMode, medicationSubmitBlocker, sessionGeneratedNotes]);

  const saveDisabledLabel = useMemo(() => {
    if (!saveDisabled) return null;
    if (medicationSubmitBlocker?.messageKey) {
      return t(medicationSubmitBlocker.messageKey);
    }
    return null;
  }, [medicationSubmitBlocker, saveDisabled, t]);

  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden ${REVIEW_PAGE_BG}`}>
      <AdverseReactionBanner flags={allFlags} />

      {taskTitle || taskPatient ? (
        <div className="shrink-0 border-b border-gray-100 px-4 pb-3 pt-4">
          {(taskPatient || displayPatient) ? (
            <p className="text-base font-bold text-gray-900">
              {taskPatient
                ? getPatientDisplayName(taskPatient, language)
                : (displayPatient?.name || '')}
              {(taskPatient?.room || displayPatient?.room)
                ? ` · ${t('room')} ${taskPatient?.room || displayPatient?.room}`
                : ''}
            </p>
          ) : null}
        </div>
      ) : (
        <PatientHeaderBar
          patient={displayPatient}
          noteTypes={detectedTypes}
          onPatientChange={syncPatientAcrossNotes}
          allowEdit={!multiPatientHandover}
          patients={multiPatientHandover ? handoverPatients : null}
          onSelectPatient={
            multiPatientHandover
              ? (index) => {
                  document
                    .getElementById(`handover-patient-${index}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              : null
          }
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-2.5">
        {(!hideEmptyTranscript || String(transcriptCleaned || transcriptRaw || transcript || '').trim()) ? (
          <div className="mb-2.5">
            <TranscriptViewer
              transcriptRaw={transcriptRaw || transcript}
              transcriptCleaned={transcriptCleaned || transcript}
            />
          </div>
        ) : null}

        {!hasMultipleNotes && !cortiFailed && sessionGeneratedNotes.length > 0 && !micBusy && (
          <ComparisonTabs activeTab={modelTab} onTabChange={setModelTab} />
        )}

        {micBusy ? (
          <NoteApplyingSkeleton />
        ) : (
          <>
            {(hasMultipleNotes || modelTab === 'openai') && renderContent()}

            {!hasMultipleNotes && modelTab === 'corti' && !cortiFailed && sessionGeneratedNotes.length > 0 && (
              <div className="mt-2.5">
                <CortiNoteDisplay
                  sections={cortiSections}
                  onSectionChange={handleCortiSectionChange}
                  patient={displayPatient}
                  datetime={encounterDateTime}
                />
              </div>
            )}
          </>
        )}

        {saveError && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {saveError}
          </div>
        )}
      </div>

      {isMedicationTaskDoc ? (
        <MedicationTaskActionBar
          onConfirmGiven={handleSave}
          onSubmitDeviation={handleSaveDeviation}
          onDiscard={onDiscard}
          confirmDisabled={saveDisabled}
          confirmDisabledLabel={saveDisabledLabel}
          patientId={recordPatient?.id || patientId || null}
        />
      ) : (
        <ActionBar
          fixed={false}
          onSave={handleSave}
          onDiscard={onDiscard}
          checkboxSave={isTaskDocumentationMode}
          saveDisabled={saveDisabled}
          saveDisabledLabel={saveDisabledLabel}
        />
      )}
      {!hideFloatingMic && floatingSlot
        && createPortal(
          <HoldToRecordButton
            patientId={recordPatient?.id || patientId || null}
            size="fab"
            anchor="inline"
            holdOptions={{ onTranscript: handleMicTranscript }}
          />,
          floatingSlot
        )}
    </div>
  );
}
